import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Smartphone, RefreshCw, Loader2, Plug, Unplug } from "lucide-react";

// Connect an athlete profile to their Trifit phone-app account and show, honestly, what came across.
export default function TrifitConnect({ athleteId, defaultEmail = "", onSynced }) {
  const [st, setSt] = useState(null);
  const [email, setEmail] = useState(defaultEmail);
  const [busy, setBusy] = useState(false);
  const [job, setJob] = useState(null);

  const load = useCallback(() => {
    if (!athleteId) return;
    api.get(`/athletes/${athleteId}/trifit-status`).then((r) => setSt(r.data)).catch(() => setSt(null));
  }, [athleteId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (defaultEmail && !email) setEmail(defaultEmail); /* eslint-disable-next-line */ }, [defaultEmail]);

  // follow the background pull so the athlete sees it finish
  useEffect(() => {
    if (!job) return;
    const id = setInterval(async () => {
      try {
        const { data } = await api.get(`/jobs/${job}`);
        if (data.status !== "running") {
          clearInterval(id);
          setJob(null);
          load();
          if (data.status === "done") {
            toast.success(data.message || "Phone data is in");
            window.dispatchEvent(new CustomEvent("trifit-synced", { detail: { athleteId } }));
            onSynced?.();
          } else toast.error(data.message || "The pull failed");
        }
      } catch { clearInterval(id); setJob(null); }
    }, 3000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job]);

  const connect = async () => {
    if (!email.trim()) return toast.error("Enter the email used in the Trifit app");
    setBusy(true);
    try {
      const { data } = await api.post(`/athletes/${athleteId}/trifit-connect`, { email: email.trim() });
      toast.success("Trifit app connected", { description: data.message });
      setJob(data.job_id);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not connect"); }
    finally { setBusy(false); }
  };

  const sync = async () => {
    setBusy(true);
    try {
      const {data:started}=await api.post(`/athletes/${athleteId}/phone-sync`);
      if(!started.ok || started.skipped==='not_connected_or_sharing_disabled')throw new Error('Phone connection is unavailable or sharing is disabled');
      const deadline=Date.now()+185000;
      let state;
      do {
        state=(await api.get(`/athletes/${athleteId}/phone-sync`)).data;
        if(state.status!=='running')break;
        await new Promise(resolve=>setTimeout(resolve,3000));
      }while(Date.now()<deadline);
      if(state.status!=='succeeded')throw new Error(state.error || 'Sync is not confirmed yet. Check the calendar sync status before retrying.');
      toast.success('Phone records imported');
      load();
      window.dispatchEvent(new CustomEvent('trifit-synced',{detail:{athleteId}}));
      onSynced?.();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message || 'Sync failed'); }
    finally { setBusy(false); }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect the Trifit app? Already-synced data stays.")) return;
    try { await api.post(`/athletes/${athleteId}/trifit-disconnect`); toast.success("Disconnected"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  if (!st) return null;

  return (
    <div className="border border-border mb-4" data-testid="trifit-connect">
      <div className="px-4 py-3 border-b border-border flex items-center gap-2 flex-wrap">
        <span className="w-2 h-2 rounded-full" style={{ background: st.connected ? "#22c55e" : "#D2378B" }} />
        <Smartphone className="w-4 h-4" />
        <span className="text-xs tracking-[0.15em] uppercase font-bold">Trifit phone app</span>
        {st.connected ? (
          <span className="text-[11px] text-muted-foreground truncate" data-testid="trifit-connected-email">{st.email}</span>
        ) : (
          <span className="text-[10px] uppercase tracking-widest text-traffic-amber" data-testid="trifit-not-connected">not connected</span>
        )}
        {st.connected && (
          <div className="ml-auto flex items-center gap-2">
            <button data-testid="trifit-sync-now" onClick={sync} disabled={busy}
              className="text-xs border border-border px-2 py-1 hover:bg-surface transition-colors flex items-center gap-1 disabled:opacity-40">
              <RefreshCw className={`w-3 h-3 ${busy ? "animate-spin" : ""}`} /> Sync now
            </button>
            <button data-testid="trifit-disconnect" onClick={disconnect}
              className="text-xs border border-border px-2 py-1 text-muted-foreground hover:text-traffic-red transition-colors flex items-center gap-1">
              <Unplug className="w-3 h-3" /> Disconnect
            </button>
          </div>
        )}
      </div>
      <div className="p-4">
        {st.connected ? (
          <div className="flex flex-wrap gap-x-6 gap-y-2 text-xs" data-testid="trifit-logs-summary">
            <span><span className="font-heading font-black text-base tabular-nums">{st.days}</span> <span className="text-muted-foreground">days of logs</span></span>
            <span><span className="font-heading font-black text-base tabular-nums">{st.sessions}</span> <span className="text-muted-foreground">sessions</span></span>
            <span className="text-muted-foreground">Data as of {st.data_as_of || "—"}</span>
            {job && <span className="flex items-center gap-1 text-muted-foreground"><Loader2 className="w-3 h-3 animate-spin" /> pulling in the background</span>}
            {!job && st.days === 0 && (
              <span className="text-traffic-amber" data-testid="trifit-no-logs">
                Connected but nothing has come across yet · ask the athlete to open the app and sync their watch, then hit Sync now.
              </span>
            )}
          </div>
        ) : (
          <div className="space-y-2">
            <p className="text-xs text-muted-foreground max-w-xl">
              Enter the email this athlete uses in the Trifit app. Their sleep, steps, workouts and food logs will be synced to the dashboard automatically.
            </p>
            <div className="flex flex-col sm:flex-row gap-2">
              <input data-testid="trifit-email-input" value={email} onChange={(e) => setEmail(e.target.value)}
                inputMode="email" autoComplete="email" placeholder="name@example.com"
                className="bg-background border border-border px-3 py-2 text-sm flex-1 min-w-0" />
              <button data-testid="trifit-connect-btn" onClick={connect} disabled={busy || !st.configured}
                title={st.configured ? "" : "Not configured on this server"}
                className="text-sm px-4 py-2 bg-foreground text-background hover:bg-neutral-800 transition-colors flex items-center justify-center gap-1.5 disabled:opacity-40">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plug className="w-4 h-4" />} Connect app
              </button>
            </div>
            {!st.configured && <p className="text-[11px] text-traffic-amber">The Trifit app link is not set up on this server yet.</p>}
          </div>
        )}
      </div>
    </div>
  );
}
